import {
  Component,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  ViewContainerRef,
} from '@angular/core';
import { forkJoin, of, Subject } from 'rxjs';
import { catchError, filter, mergeMap, takeUntil } from 'rxjs/operators';
import { ActivatedRoute, ParamMap } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';

import { Client, ExternalClientDataTypes } from '../../../core/types/client';
import { IdentityReportsService } from '../../../core/services/identity-reports.service';
import { ClientService } from '../../../core/services/client.service';
import { DialogProviderService } from '@app/core/services/dialog-provider.service';
import { NotificationService } from '../../../core/services/notification.service';
import { ErrorService } from '../../../core/services/error.service';
import { ListElement, MIReportData, IdentityReport, ClientMapperTypes, ClientMIMapperTypes, MiSummary } from '../../../core/types/identity-reports';
import { ItfgDateFormatPipe } from '@app/shared/pipes/itfg-date-format.pipe';

type ReportStatus = 'success' | 'incorrect_input' | 'not_found' | 'server_timeout' | 'server_error';

const STATUS_TRANSLATION_KEYS: Record<Exclude<ReportStatus, 'success'>, string> = {
  incorrect_input: 'mi.incorrectDataMsg',
  not_found: 'mi.noMiData',
  server_timeout: 'reports.serverTimeoutError',
  server_error: 'reports.serverGenericError',
};

@Component({
  selector: 'itfg-mi-report',
  templateUrl: './mi-report.component.html',
  styleUrls: ['./mi-report.component.scss'],
  standalone: false,
})
export class MiReportComponent implements OnInit, OnChanges, OnDestroy {

  @Input() client: Client;

  reportData: IdentityReport<MIReportData>;
  clientReportData: MIReportData;
  /** The report as read by identity-reports - see MiSummary. */
  miSummary: MiSummary;
  miListData: ListElement[];
  requestTime: Date;
  isReport = false;
  isListData = false;

  /** Resolved translation string for the current non-success status. */
  statusMessage = '';

  public CLIENT_MAPPER: { [key: string]: string };


  private readonly _unsubscribe = new Subject<void>();

  constructor(
    private readonly identityReportsService: IdentityReportsService,
    private readonly clientService: ClientService,
    private readonly translate: TranslateService,
    private readonly route: ActivatedRoute,
    private readonly _viewContainerRef: ViewContainerRef,
    private readonly dialogService: DialogProviderService,
    private readonly notificationService: NotificationService,
    private readonly errorService: ErrorService,
    private datePipe: ItfgDateFormatPipe,
  ) {
    this.CLIENT_MAPPER = {
      [ClientMapperTypes.ID_CARD_NUMBER]: ClientMIMapperTypes.IDENTITY_DOCUMENT_NUMBER,
      [ClientMapperTypes.ID_CARD_ISSUED_DATE]: ClientMIMapperTypes.ISSUE_DATE,
      [ClientMapperTypes.ID_CARD_ISSUER_NAME]: ClientMIMapperTypes.ISSUER_NAME,
      [ClientMapperTypes.FIRST_NAME]: ClientMIMapperTypes.PERSONAL_NAMES_FIRST_NAME,
      [ClientMapperTypes.MIDDLE_NAME]: ClientMIMapperTypes.PERSONAL_NAMES_SURNAME,
      [ClientMapperTypes.LAST_NAME]: ClientMIMapperTypes.PERSONAL_NAMES_FAMILY_NAME,
      [ClientMapperTypes.ID_CARD_ADDRESS_CITY_NAME]: ClientMIMapperTypes.PERMANENT_ADDRESS_SETTLEMENT_NAME,
      [ClientMapperTypes.ID_CARD_ADDRESS_ADDRESS]: ClientMIMapperTypes.ADDRESS,
      [ClientMapperTypes.NATIONALITY_NAME]: ClientMIMapperTypes.NATIONALITY_NAME,
      [ClientMapperTypes.SECOND_NATIONALITY_NAME]: ClientMIMapperTypes.SECOND_NATIONALITY_NAME,
    };
  }

  // ── Lifecycle ──────────────────────────────────────────────────────

  ngOnInit(): void {
    this.route.paramMap
      .pipe(
        filter((params: ParamMap) => typeof params.get('id') === 'string'),
        mergeMap((params: ParamMap) => this.clientService.getClientById(Number(params.get('id')))),
        takeUntil(this._unsubscribe),
      )
      .subscribe({
        next: client => {
          this.client = client;
          if (client?.id) this._loadReports();
        },
        error: error => this.errorService.handleError(error, this.route.data),
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.client?.id) this._loadReports();
  }

  get config() {
    // The client is compared against the summary - CLIENT_MAPPER points at its fields.
    return {
      reportType: ExternalClientDataTypes.MI,
      reportData: this.miSummary,
      client: this.client,
      clientMapper: this.CLIENT_MAPPER,
      criticalProperties: [
        ClientMapperTypes.FIRST_NAME,
        ClientMapperTypes.MIDDLE_NAME,
        ClientMapperTypes.LAST_NAME
      ],
    };
  }


  // ── Actions ────────────────────────────────────────────────────────

  miDataChanged(event: any): void {
    this._fetchReport(event.value.id);
  }

  getNewMi(): void {
    this.translate
      .get(['mi.getNewMiReportWarning', 'mi.miReportCriteriaMissing', 'global.confirm', 'global.cancel', 'mi.getNewMiReport'])
      .subscribe(t => {
        this.dialogService
          .openConfirm({
            message: t['mi.getNewMiReportWarning'],
            disableClose: false,
            viewContainerRef: this._viewContainerRef,
            title: t['mi.getNewMiReport'],
            cancelButton: t['global.cancel'],
            acceptButton: t['global.confirm'],
          })
          .afterClosed()
          .subscribe((accept: boolean) => {
            if (!accept) return;

            if (!this.client.civilId || !this.client.idCardNumber) {
              this.notificationService.showLocalizedSnackbarNotification({
                notificationText: t['mi.miReportCriteriaMissing'],
                action: 'error',
              });
              return;
            }

            this.identityReportsService
              .newMiReport(this.client.civilId, this.client.idCardNumber)
              .subscribe(report => {
                // The summary is read back by id so the card and the comparison always
                // come from the same report.
                this._fetchReport(report.id);
                this._refreshList();
              });
          });
      });
  }

  // ── Private ────────────────────────────────────────────────────────

  private _loadReports(): void {
    this.clientReportData = {};
    this.reportData = null;
    this.miSummary = null;

    this.identityReportsService
      .getMiReportsList(this.client.civilId)
      .subscribe(list => {
        this.miListData = list.map(item => ({ ...item, time: new Date(item.time) }));
        this.isListData = list.length > 0;

        if (this.isListData) {
          this._fetchReport(this.miListData[0].id);
        } else {
          this.isReport = false;
        }
      });
  }

  private _fetchReport(id: string): void {
    forkJoin({
      report: this.identityReportsService.getMIReportById(this.client.civilId, id),
      // A summary that cannot be read must not take the whole card down with it.
      summary: this.identityReportsService
        .getMiReportSummary(this.client.civilId, id)
        .pipe(catchError(() => of(null as MiSummary))),
    }).subscribe(({ report, summary }) => this._applyReport(report, summary));
  }

  private _applyReport(report: IdentityReport<MIReportData>, summary: MiSummary): void {
    this.reportData = report;
    this.requestTime = new Date(report.time);
    this.clientReportData = report.data;
    this.miSummary = summary;
    this.isReport = true;
    this._resolveStatusMessage();
  }

  private _refreshList(): void {
    this.identityReportsService
      .getMiReportsList(this.client.civilId)
      .subscribe(list => {
        this.miListData = list.map(item => ({ ...item, time: new Date(item.time) }));
        this.isListData = list.length > 0;
      });
  }

  private _resolveStatusMessage(): void {
    const status = this.reportData?.status as ReportStatus;
    const key = STATUS_TRANSLATION_KEYS[status as Exclude<ReportStatus, 'success'>];
    if (!key) { this.statusMessage = ''; return; }

    const params = (status === 'server_timeout' || status === 'server_error')
      ? { requestTime: this.datePipe.transform(this.requestTime) }
      : {};

    this.translate.get(key, params).subscribe(msg => { this.statusMessage = msg; });
  }

  ngOnDestroy(): void {
    this._unsubscribe.next();
    this._unsubscribe.complete();
  }
}